import { Dispatch, useMemo } from 'react';

import {
  ChakraFlex,
  ChakraText,
  CircularProgress,
  EquipUITheme,
} from '@equip.health/ui';
import { DateTime } from 'luxon';

import {
  AssessmentAction,
  CreateAssessmentIntent,
  PROFILE_SURVEY_BANNER_STYLES,
  UpdateAssessmentIntent,
} from '~/lib/constants/patients.constants';
import { SurveyName } from '~/lib/constants/survey/SurveyName';
import { SurveyResponseStatus } from '~/lib/constants/survey/SurveyResponseStatus';
import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import { generatePDF, stringToJson } from '~/lib/util/survey.util';
import util from '~/lib/util/util';

import SurveyList from './SurveyList';
import SurveyResults from './SurveyResults';

const { paragraph } = EquipUITheme.typography;

type SurveyHomeProps = {
  assessmentList: SurveyResponse[];
  isError: boolean;
  isLoading: boolean;
  onSendEmail: (assessment: Partial<SurveyResponse>) => void;
  onUpdateAssessmentStatus: (
    status: SurveyResponseStatus,
    toUserEmail: string,
    responseExternalId: string,
  ) => void;
  patientFirstName: string;
  patientLastName: string;
  selectedSurveyResults: Partial<SurveyResponse>;
  surveys: Survey[];
  updateAssessmentState: Dispatch<AssessmentAction>;
};

const SurveyHome = ({
  assessmentList,
  isError,
  isLoading,
  onSendEmail,
  onUpdateAssessmentStatus,
  patientFirstName,
  patientLastName,
  selectedSurveyResults,
  surveys,
  updateAssessmentState,
}: SurveyHomeProps) => {
  const {
    endDate: selectedAssessmentEndDate,
    jsonResults: selectedAssessmentJsonResults,
    surveyDefinition: selectedSurveyDefinition,
    surveyName: selectedSurveyName,
  } = selectedSurveyResults ?? {};

  const { definition, json } = {
    definition: stringToJson(selectedSurveyDefinition),
    json: stringToJson(selectedAssessmentJsonResults),
  };

  const {
    addEditSurveyRespondentAccordion,
    setComponentVisibility,
    surveyAccordion,
  } = useComponentPortalContext();

  /* Open survey accordion and load current assessment data */
  const handleStartSurveyButtonClick = (
    assessment: Partial<SurveyResponse>,
  ): void => {
    const existing = (assessmentList ?? []).find(
      ({ responseExternalId }: SurveyResponse) =>
        responseExternalId === assessment.responseExternalId,
    );

    setComponentVisibility({
      component: PortalComponent.SURVEY_ACCORDION,
      data: existing,
      isExpanded: true,
      isVisible: true,
    });
  };

  /** Primary CTA handler corresponding to the assessment status */
  const primaryCTAOnClickMapper: Record<
    SurveyResponseStatus,
    (assessment?: Partial<SurveyResponse>) => void
  > = {
    [SurveyResponseStatus.NEW]: (assessment: Partial<SurveyResponse>) => {
      if (assessment?.responseExternalId) {
        handleStartSurveyButtonClick(assessment);
      } else {
        updateAssessmentState({
          data: {
            createAssessmentIntent: CreateAssessmentIntent.BEGIN_NOW,
            selectedAssessment: assessment,
          },
          field: 'SET',
        });
      }
    },
    [SurveyResponseStatus.REQUESTED]: (assessment: Partial<SurveyResponse>) =>
      handleStartSurveyButtonClick(assessment),
    [SurveyResponseStatus.INCOMPLETE]: (assessment: Partial<SurveyResponse>) =>
      updateAssessmentState({
        data: assessment,
        field: 'selectedSurveyResults',
      }),
    [SurveyResponseStatus.IN_PROGRESS]: (assessment: Partial<SurveyResponse>) =>
      handleStartSurveyButtonClick(assessment),
    [SurveyResponseStatus.DONE]: (assessment: Partial<SurveyResponse>) =>
      updateAssessmentState({
        data: assessment,
        field: 'selectedSurveyResults',
      }),
    [SurveyResponseStatus.VOID]: (_assessment: Partial<SurveyResponse>) => {
      // intentionally empty
    },
  };

  /** Secondary CTA handler corresponding to the assessment status */
  const secondaryCTAOnClickMapper: Record<
    SurveyResponseStatus,
    (assessment: Partial<SurveyResponse>) => void
  > = {
    [SurveyResponseStatus.NEW]: (assessment: Partial<SurveyResponse>) => {
      if (assessment.toUserEmail) {
        updateAssessmentState({
          data: {
            emailSentData: assessment,
            updateAssessmentIntent: UpdateAssessmentIntent.SEND_INVITATION,
          },
          field: 'SET',
        });
        onUpdateAssessmentStatus(
          SurveyResponseStatus.REQUESTED,
          assessment.toUserEmail,
          assessment.responseExternalId,
        );
      } else {
        updateAssessmentState({
          data: {
            createAssessmentIntent: CreateAssessmentIntent.SEND_INVITATION,
            selectedAssessment: assessment,
          },
          field: 'SET',
        });
      }
    },
    [SurveyResponseStatus.REQUESTED]: (assessment: Partial<SurveyResponse>) =>
      onSendEmail(assessment),
    [SurveyResponseStatus.IN_PROGRESS]: (assessment: Partial<SurveyResponse>) =>
      onSendEmail(assessment),
    [SurveyResponseStatus.INCOMPLETE]: ({
      endDate,
      jsonResults,
      surveyDefinition,
      surveyName,
    }) =>
      generatePDF({
        endDate,
        jsonResults: util.parseJson(jsonResults),
        patientName: `${patientFirstName} ${patientLastName}`,
        surveyDefinition: util.parseJson(surveyDefinition),
        surveyName,
      }),
    [SurveyResponseStatus.DONE]: ({
      endDate,
      jsonResults,
      surveyDefinition,
      surveyName,
    }) =>
      generatePDF({
        endDate,
        jsonResults: util.parseJson(jsonResults),
        patientName: `${patientFirstName} ${patientLastName}`,
        surveyDefinition: util.parseJson(surveyDefinition),
        surveyName,
      }),
    [SurveyResponseStatus.VOID]: (_assessment: Partial<SurveyResponse>) => {
      // intentionally empty
    },
  };

  const handleSurveyCTAClick = (
    survey: SurveyResponse,
    isPrimaryCTA: boolean,
  ): void => {
    updateAssessmentState({ data: null, field: 'emailSentData' });
    if (isPrimaryCTA) primaryCTAOnClickMapper[survey.status](survey);
    else secondaryCTAOnClickMapper[survey.status](survey);
  };

  const handleSurveyEditClick = (assessment: SurveyResponse): void => {
    updateAssessmentState({
      data: {
        selectedAssessment: assessment,
        updateAssessmentIntent: UpdateAssessmentIntent.UPDATE_RESPONDENT,
      },
      field: 'SET',
    });
  };

  const admissionsIntakeAssessment = useMemo(() => {
    if (isLoading || isError) return null;

    const { surveyExternalId, name: surveyName } =
      surveys?.find(({ name }) => name === SurveyName.ADMISSIONS_INTAKE) ?? {};

    if (!surveyExternalId) return null;

    const existing = (assessmentList ?? []).find(
      ({ status, surveyName: _surveyName }: SurveyResponse) =>
        _surveyName === SurveyName.ADMISSIONS_INTAKE &&
        status !== SurveyResponseStatus.VOID,
    );

    return (
      existing ??
      ({
        status: SurveyResponseStatus.NEW,
        surveyExternalId,
        surveyName,
      } as SurveyResponse)
    );
  }, [assessmentList, isError, isLoading, surveys]);

  return (
    <>
      {isLoading ? (
        <CircularProgress
          id="patient-assessments-loading"
          isIndeterminate
          padding="8px"
          size="30px"
        />
      ) : (
        <ChakraFlex
          {...PROFILE_SURVEY_BANNER_STYLES}
          backgroundColor="primary.10"
          borderRadius="16px"
          marginBottom="24px"
          marginTop="8px"
        >
          {isError ? (
            <ChakraText {...paragraph}>
              Error retrieving assessment list
            </ChakraText>
          ) : (
            <SurveyList
              isDisabled={
                isLoading ||
                Boolean(
                  addEditSurveyRespondentAccordion?.isVisible ||
                    surveyAccordion?.isVisible,
                )
              }
              onCTAClick={handleSurveyCTAClick}
              onEditClick={handleSurveyEditClick}
              patientFirstName={`${patientFirstName} ${patientLastName}`}
              surveys={
                admissionsIntakeAssessment ? [admissionsIntakeAssessment] : []
              }
            />
          )}
        </ChakraFlex>
      )}
      <SurveyResults
        isOpen={!!selectedSurveyResults}
        onClose={() =>
          updateAssessmentState({ data: null, field: 'selectedSurveyResults' })
        }
        patientName={`${patientFirstName} ${patientLastName}`}
        surveyCompletionDate={
          !selectedAssessmentEndDate
            ? null
            : DateTime.fromISO(selectedAssessmentEndDate).toFormat('LL/dd/yyyy')
        }
        surveyDefinition={definition}
        surveyName={selectedSurveyName}
        surveyResults={json}
      />
    </>
  );
};

export default SurveyHome;
